<script setup>
// Wybór dnia wpisu — dwa tygodnie w tył i w przód, z podpowiedzią
// „dzisiaj / wczoraj / jutro", żeby nie liczyć dat w głowie.
import { computed } from 'vue';
import SelectField from '@/components/v2/select-field.vue';
import { intlLocale } from '@/composables/useLanguage.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    draft: { type: Object, required: true },
    // Puste = „Dzień" w języku aplikacji.
    label: { type: String, default: '' },
    inputId: { type: String, default: 'v2-day' },
});

const { t } = useI18n();

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

const dayLabel = (d) => {
    const wd = d.toLocaleDateString(intlLocale(), { weekday: 'long' });
    const dm = d.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' });
    const diff = Math.round((d - today0()) / 86400000);
    const rel = diff === 0 ? ` · ${t('entry.daySelect.today')}` : diff === -1 ? ` · ${t('entry.daySelect.yesterday')}` : diff === 1 ? ` · ${t('entry.daySelect.tomorrow')}` : '';
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${dm}${rel}`;
};

const options = computed(() => {
    const base = today0();
    const days = [];
    for (let i = -14; i <= 14; i++) {
        const d = new Date(base);
        d.setDate(d.getDate() + i);
        days.push(d);
    }
    // Dzień oglądany w planerze może wypaść poza tym zakresem.
    if (!days.some(d => ymd(d) === ymd(props.draft.day))) {
        days.push(new Date(props.draft.day));
        days.sort((a, b) => a - b);
    }
    return days.map(d => ({ value: ymd(d), label: dayLabel(d) }));
});

const selected = computed({
    get: () => ymd(props.draft.day),
    set: (value) => {
        const [y, m, d] = value.split('-').map(Number);
        props.draft.day = new Date(y, m - 1, d);
    },
});
</script>

<template>
    <div>
        <label class="v2-label" :for="inputId">{{ label || t('entry.daySelect.label') }}</label>
        <SelectField :id="inputId" v-model="selected" :options="options" :search-placeholder="t('entry.daySelect.search')" />
    </div>
</template>
